import React from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import Timeline from "@mui/lab/Timeline";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineOppositeContent from "@mui/lab/TimelineOppositeContent";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { List, useDataGrid } from "@refinedev/mui";
import { useCreate, useInvalidate, useOne, useUpdate } from "@refinedev/core";
import { Link as RouterLink } from "react-router";
import { axiosInstance } from "@/providers/data";
import { EVIDENCE_RESOURCE, type EvidenceHoldListItem } from "@/lib/evidence";

const RESOURCE = "admin/removal-requests";

type RemovalStatus = "received" | "triaging" | "removed" | "rejected";
type RemovalLogAction = RemovalStatus | "acknowledged" | "notified" | "note";

// One row of GET /api/v2/admin/removal-requests. No user reference anywhere:
// the filer is usually the SUBJECT of the imagery, not an account, and the row
// deliberately carries their name and email as plain text (server schema note).
type RemovalRequestRecord = {
  id: number;
  reference: string;
  filerName: string;
  filerEmail: string;
  contentRef: string;
  status: RemovalStatus;
  receivedAt: string;
  dueAt: string;
  firstActionedAt: string | null;
  resolvedAt: string | null;
  resolutionNote: string | null;
  assignedTo: number | null;
  assigneeName: string | null;
  sourceIp: string | null;
  userAgent: string | null;
  // Set by the moderator when the person depicted may be a minor. With it set,
  // the server refuses `removed` until an evidence hold is placed for the
  // request: removing has to mean unpublish-and-preserve, never delete.
  involvesMinor: boolean;
  // Whole hours to `dueAt`, computed server-side so the queue can sort on it.
  // Negative once breached.
  hoursRemaining: number;
};

// One row of the append-only log. `actorId` is null for the system-written
// entries (intake, acknowledgement email).
type RemovalLogEntry = {
  id: number;
  requestId: number;
  action: RemovalLogAction;
  actorId: number | null;
  note: string | null;
  at: string;
};

// GET /api/v2/admin/removal-requests/:id — the request plus its log. The log IS
// the compliance view: it is what "we acted within 48 hours" is proven from.
type RemovalDetail = {
  request: Omit<RemovalRequestRecord, "assigneeName" | "hoursRemaining">;
  log: RemovalLogEntry[];
  // The evidence holds placed for this request — ids and states, never the material.
  holds: EvidenceHoldListItem[];
};

type HoldTargetType = "upload" | "story" | "creator";

const HOLD_TARGET_LABEL: Record<HoldTargetType, string> = {
  upload: "Upload (creator library id)",
  story: "Story (library id)",
  creator: "Account (user id)",
};

const STATUSES: readonly RemovalStatus[] = ["received", "triaging", "removed", "rejected"];
const TERMINAL: readonly RemovalStatus[] = ["removed", "rejected"];

const STATUS_COLOR: Record<RemovalStatus, "warning" | "info" | "success" | "default"> = {
  received: "warning",
  triaging: "info",
  removed: "success",
  rejected: "default",
};

const ACTION_LABEL: Record<RemovalLogAction, string> = {
  received: "Received",
  acknowledged: "Acknowledgement emailed",
  triaging: "Triage started",
  removed: "Removed",
  rejected: "Rejected",
  // Written by the resolution email on a terminal transition, never by a person.
  notified: "Filer emailed the outcome",
  note: "Note",
};

const HOUR_MS = 60 * 60 * 1000;

// Classified against the precise deadline, not the row's whole-hour
// `hoursRemaining`: that value is floored, so `0` still means up to 59 minutes
// left and must not read as overdue. Amber inside the sweep's 12-hour warning
// window, red at or past the deadline. The list refetches every minute, so a
// row re-renders as its state changes.
const deadlineState = (
  dueAt: string,
  now: number = Date.now()
): { color: "error" | "warning" | "default"; label: string } => {
  const msLeft = new Date(dueAt).getTime() - now;
  if (Number.isNaN(msLeft)) {
    return { color: "default", label: "-" };
  }
  if (msLeft <= 0) {
    const overdue = -msLeft;
    const label = overdue < HOUR_MS ? `${Math.ceil(overdue / 60_000)}m overdue` : `${Math.floor(overdue / HOUR_MS)}h overdue`;
    return { color: "error", label };
  }
  const label = msLeft < HOUR_MS ? `${Math.ceil(msLeft / 60_000)}m left` : `${Math.floor(msLeft / HOUR_MS)}h left`;
  return { color: msLeft < 12 * HOUR_MS ? "warning" : "default", label };
};

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
};

// Local calendar day, for `<input type="date">`. `toISOString()` would give the
// UTC day, which is yesterday or tomorrow for an operator near local midnight.
const isoDate = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

// The shared axios instance rejects with Refine's plain `HttpError` object, not
// an `Error` instance, so `instanceof Error` would hide the server's message.
const errorMessage = (err: unknown): string | undefined => {
  if (err instanceof Error) {
    return err.message;
  }
  if (err && typeof err === "object" && "message" in err && typeof err.message === "string") {
    return err.message;
  }
  return undefined;
};

// The queue is a clock: `hoursRemaining` and the deadline colouring only move
// when the list is refetched, so an operator who leaves it open would watch a
// row sit neutral past its 12-hour mark and never see a new filing. A minute,
// not the 5s the live generation/session lists use — a deadline moves by the
// hour.
const REFRESH_INTERVAL_MS = 60_000;

/**
 * The SLA report — `GET reports/sla?from=&to=&format=csv` — is the artefact
 * handed to counsel or a regulator. Fetched through the shared axios instance
 * rather than a plain link: token transfer is header-based, so an `<a href>`
 * would arrive unauthenticated.
 */
const SlaExport = () => {
  const today = new Date();
  const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
  const [from, setFrom] = React.useState(isoDate(monthAgo));
  const [to, setTo] = React.useState(isoDate(today));
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const download = async () => {
    setBusy(true);
    setError(null);
    try {
      // Not `responseType: "blob"`: that would also deliver a 4xx JSON body as
      // a Blob, which the shared interceptor cannot read a message out of. As
      // text, a CSV comes through untouched and an error body is parsed and
      // surfaced like every other request's.
      const response = await axiosInstance.get<string>(`/api/v2/${RESOURCE}/reports/sla`, {
        // A date-only `to` means the whole of that day server-side.
        params: { from, to, format: "csv" },
      });
      const url = URL.createObjectURL(new Blob([response.data], { type: "text/csv;charset=utf-8" }));
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `removal-sla-${from}_${to}.csv`;
      // In the document for the browsers that need it, and the URL kept alive
      // until the download has been queued: revoking synchronously after click()
      // can pull the object out from under a download that has not started yet.
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      setError(errorMessage(err) ?? "Could not download the report.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
      <TextField
        type="date"
        size="small"
        label="From"
        value={from}
        onChange={(event) => setFrom(event.target.value)}
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        type="date"
        size="small"
        label="To"
        value={to}
        onChange={(event) => setTo(event.target.value)}
        InputLabelProps={{ shrink: true }}
      />
      <Button
        variant="outlined"
        size="small"
        startIcon={<DownloadIcon />}
        disabled={busy || !from || !to || from > to}
        onClick={() => void download()}
      >
        SLA report (CSV)
      </Button>
      {error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}
    </Stack>
  );
};

const LogTimeline = ({ log }: { log: RemovalLogEntry[] }) => (
  <Timeline
    sx={{
      p: 0,
      m: 0,
      "& .MuiTimelineOppositeContent-root": { flex: 0.45, pl: 0 },
    }}
  >
    {log.map((entry, index) => (
      <TimelineItem key={entry.id}>
        <TimelineOppositeContent color="text.secondary" sx={{ fontSize: 12 }}>
          {formatDateTime(entry.at)}
        </TimelineOppositeContent>
        <TimelineSeparator>
          <TimelineDot
            variant={entry.action === "note" ? "outlined" : "filled"}
            color={
              entry.action === "removed"
                ? "success"
                : entry.action === "rejected"
                  ? "grey"
                  : entry.action === "triaging"
                    ? "info"
                    : entry.action === "received"
                      ? "warning"
                      : "grey"
            }
          />
          {index < log.length - 1 && <TimelineConnector />}
        </TimelineSeparator>
        <TimelineContent sx={{ pb: 2 }}>
          <Typography variant="body2">{ACTION_LABEL[entry.action] ?? entry.action}</Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            {entry.actorId === null ? "system" : `user #${entry.actorId}`}
          </Typography>
          {entry.note && (
            <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>
              {entry.note}
            </Typography>
          )}
        </TimelineContent>
      </TimelineItem>
    ))}
  </Timeline>
);

/**
 * The detail drawer: the request, the log as a timeline, and the two writes —
 * a status change (with a note, mandatory before `removed` or `rejected`) and
 * a standalone note. Both append to the log; nothing here edits or removes.
 */
const RemovalDetailDrawer = ({ id, onClose }: { id: number | null; onClose: () => void }) => {
  const invalidate = useInvalidate();
  const detail = useOne<RemovalDetail>({
    resource: RESOURCE,
    id: id ?? undefined,
    queryOptions: { enabled: id !== null },
  });
  const { mutate: update, mutation: updateMutation } = useUpdate<RemovalRequestRecord>();
  const { mutate: create, mutation: createMutation } = useCreate<RemovalLogEntry>();
  const { mutate: placeHold, mutation: holdMutation } = useCreate<EvidenceHoldListItem>();
  const [holdTargetType, setHoldTargetType] = React.useState<HoldTargetType>("upload");
  const [holdTargetId, setHoldTargetId] = React.useState("");
  const [holdNote, setHoldNote] = React.useState("");

  const [status, setStatus] = React.useState<RemovalStatus>("triaging");
  const [note, setNote] = React.useState("");
  const [standaloneNote, setStandaloneNote] = React.useState("");

  const request = detail.result?.request;
  const log = detail.result?.log ?? [];
  const holds = detail.result?.holds ?? [];

  // Follow the loaded record rather than a stale local pick when the drawer
  // moves to another request — and start clean whenever it opens at all: keyed
  // on `id` too, because reopening the SAME request hands back the cached
  // record unchanged, and an effect keyed only on it would leave an unsaved
  // status, note or draft sitting in the controls to be submitted later.
  React.useEffect(() => {
    setStatus(request?.status ?? "triaging");
    setNote("");
    setStandaloneNote("");
    setHoldTargetId("");
    setHoldNote("");
  }, [id, request]);

  // The request a mutation was submitted FOR, not the one the drawer shows
  // when it resolves: an operator can close this drawer and open another while
  // a PATCH is in flight, and the callback must then refresh the submitted
  // request and leave the newly opened one's draft alone.
  const shownId = React.useRef(id);
  shownId.current = id;
  const refreshRequest = (submitted: number) => {
    void invalidate({ resource: RESOURCE, invalidates: ["list", "detail"], id: submitted });
  };

  // Cleared here, not only in the effect above: after a refetch react-query
  // may hand back a structurally identical `request`, in which case the effect
  // does not run and a submitted note would sit in its field ready to be
  // appended twice.
  const afterStatusChange = (submitted: number) => () => {
    if (shownId.current === submitted) {
      setNote("");
    }
    refreshRequest(submitted);
  };
  const afterNote = (submitted: number) => () => {
    if (shownId.current === submitted) {
      setStandaloneNote("");
    }
    refreshRequest(submitted);
  };

  const terminalWithoutNote = TERMINAL.includes(status) && note.trim().length === 0;
  const unchanged = request?.status === status && note.trim().length === 0;

  const submitStatus = () => {
    if (id === null) {
      return;
    }
    update(
      {
        resource: RESOURCE,
        id,
        values: { status, note: note.trim() || undefined },
        successNotification: { message: `Marked ${status}`, type: "success" },
      },
      { onSuccess: afterStatusChange(id) }
    );
  };

  const setInvolvesMinor = (involvesMinor: boolean) => {
    if (id === null) {
      return;
    }
    update(
      {
        resource: RESOURCE,
        id: `${id}/involves-minor`,
        values: { involvesMinor },
        successNotification: {
          message: involvesMinor ? "Marked as possibly involving a minor" : "Minor flag cleared",
          type: "success",
        },
      },
      { onSuccess: () => refreshRequest(id) }
    );
  };

  const holdTargetIdNumber = Number(holdTargetId);
  const holdTargetValid = Number.isSafeInteger(holdTargetIdNumber) && holdTargetIdNumber > 0;

  const submitHold = () => {
    if (id === null || !holdTargetValid) {
      return;
    }
    placeHold(
      {
        resource: EVIDENCE_RESOURCE,
        values: {
          trigger: "removal_request",
          removalRequestId: id,
          targetType: holdTargetType,
          targetId: holdTargetIdNumber,
          unpublish: holdTargetType === "upload" ? true : undefined,
          note: holdNote.trim() || undefined,
        },
        successNotification: { message: "Evidence hold placed — copies are being made", type: "success" },
      },
      {
        onSuccess: () => {
          if (shownId.current === id) {
            setHoldTargetId("");
            setHoldNote("");
          }
          refreshRequest(id);
        },
      }
    );
  };

  const submitNote = () => {
    if (id === null || standaloneNote.trim().length === 0) {
      return;
    }
    create(
      {
        resource: `${RESOURCE}/${id}/notes`,
        values: { note: standaloneNote.trim() },
        successNotification: { message: "Note added", type: "success" },
      },
      { onSuccess: afterNote(id) }
    );
  };

  return (
    <Drawer anchor="right" open={id !== null} onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", sm: 520 } } }}>
      <Box sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" sx={{ fontFamily: "monospace" }}>
            {request?.reference ?? "…"}
          </Typography>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        {detail.query.isError && <Alert severity="error">Could not load this request.</Alert>}

        {request && (
          <>
            <Stack spacing={0.5} sx={{ mt: 1 }}>
              <Typography variant="body2">
                <strong>{request.filerName}</strong> · {request.filerEmail}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Received {formatDateTime(request.receivedAt)} · due {formatDateTime(request.dueAt)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                First actioned {formatDateTime(request.firstActionedAt)} · resolved{" "}
                {formatDateTime(request.resolvedAt)}
              </Typography>
              {request.sourceIp && (
                <Typography variant="caption" color="text.secondary">
                  Source (abuse triage only): {request.sourceIp}
                </Typography>
              )}
            </Stack>

            <Typography variant="overline" color="text.secondary" component="div" sx={{ mt: 2 }}>
              Content reference (filer-supplied — data, not a link to follow blindly)
            </Typography>
            <Typography
              variant="body2"
              component="pre"
              sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "inherit", m: 0 }}
            >
              {request.contentRef}
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Typography variant="overline" color="text.secondary" component="div">
              Person depicted may be a minor
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={request.involvesMinor}
                  disabled={updateMutation.isPending}
                  onChange={(event) => setInvolvesMinor(event.target.checked)}
                />
              }
              label={request.involvesMinor ? "Involves a minor" : "No"}
            />
            {request.involvesMinor && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Place an evidence hold before marking this removed: preserve, delete nothing, and tell the safety
                reviewers. Never open, download or forward the content yourself.
              </Alert>
            )}

            <Typography variant="overline" color="text.secondary" component="div" sx={{ mt: 2 }}>
              Evidence holds
            </Typography>
            {holds.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                None placed for this request.
              </Typography>
            ) : (
              <Stack spacing={0.5}>
                {holds.map((hold) => (
                  <Stack key={hold.publicId} direction="row" spacing={1} alignItems="center">
                    <Link component={RouterLink} to={`/evidence/${hold.publicId}`} sx={{ fontFamily: "monospace" }}>
                      {hold.publicId}
                    </Link>
                    <Chip size="small" label={hold.status} />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={hold.preserveState}
                      color={hold.preserveState === "preserved" ? "success" : hold.preserveState === "failed" ? "error" : "warning"}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {hold.targetType} {hold.targetRef}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}
            <Stack spacing={1} sx={{ mt: 1 }}>
              <Stack direction="row" spacing={1}>
                <TextField
                  select
                  size="small"
                  label="Hold what"
                  value={holdTargetType}
                  onChange={(event) => setHoldTargetType(event.target.value as HoldTargetType)}
                  sx={{ minWidth: 220 }}
                >
                  {(Object.keys(HOLD_TARGET_LABEL) as HoldTargetType[]).map((value) => (
                    <MenuItem key={value} value={value}>
                      {HOLD_TARGET_LABEL[value]}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  label="Id"
                  value={holdTargetId}
                  onChange={(event) => setHoldTargetId(event.target.value.trim())}
                  error={holdTargetId !== "" && !holdTargetValid}
                  sx={{ width: 140 }}
                />
              </Stack>
              {holdTargetType === "upload" && (
                <Typography variant="caption" color="text.secondary">
                  The upload comes down once every copy is verified. Mark the request removed after that.
                </Typography>
              )}
              <TextField
                size="small"
                label="Note (optional)"
                value={holdNote}
                onChange={(event) => setHoldNote(event.target.value)}
              />
              <Button
                variant="outlined"
                color="warning"
                size="small"
                disabled={!holdTargetValid || holdMutation.isPending}
                onClick={submitHold}
              >
                Place evidence hold
              </Button>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography variant="overline" color="text.secondary" component="div">
              Status
            </Typography>
            <Stack spacing={1} sx={{ mt: 1 }}>
              <TextField
                select
                size="small"
                label="Move to"
                value={status}
                onChange={(event) => setStatus(event.target.value as RemovalStatus)}
              >
                {STATUSES.map((value) => (
                  <MenuItem key={value} value={value}>
                    <Chip size="small" label={value} color={STATUS_COLOR[value]} />
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label={TERMINAL.includes(status) ? "Resolution note (required)" : "Note (optional)"}
                multiline
                minRows={2}
                value={note}
                onChange={(event) => setNote(event.target.value)}
                helperText={
                  TERMINAL.includes(status)
                    ? "What was removed (or why not). This is the record handed to counsel."
                    : undefined
                }
              />
              <Button
                variant="contained"
                size="small"
                disabled={terminalWithoutNote || unchanged || updateMutation.isPending}
                onClick={submitStatus}
              >
                Update status
              </Button>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography variant="overline" color="text.secondary" component="div">
              Add a note without changing status
            </Typography>
            <Stack spacing={1} sx={{ mt: 1 }}>
              <TextField
                size="small"
                label="Note"
                multiline
                minRows={2}
                placeholder="Called the filer; they confirmed the URL."
                value={standaloneNote}
                onChange={(event) => setStandaloneNote(event.target.value)}
              />
              <Button
                variant="outlined"
                size="small"
                disabled={standaloneNote.trim().length === 0 || createMutation.isPending}
                onClick={submitNote}
              >
                Add note
              </Button>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography variant="overline" color="text.secondary" component="div">
              Log (append-only)
            </Typography>
            <LogTimeline log={log} />
          </>
        )}
      </Box>
    </Drawer>
  );
};

export const RemovalRequestList = () => {
  // "open" is every unresolved request — received AND triaging — which is what
  // the person working the queue wants to see. Server-side pseudo-filter.
  const [status, setStatus] = React.useState<string>("open");
  const [selected, setSelected] = React.useState<number | null>(null);

  // Server-ordered: DEADLINE SOONEST, not newest first. This is a clock, not a
  // backlog — the row that matters is the one about to breach. Columns are not
  // sortable for the same reason as the other queues: the shared data provider
  // sends page/limit only, so client-side sorting would reorder just one page.
  //
  // The status entry is always present, `undefined` for "All": Refine seeds its
  // table state with the first permanent filter (`open`) and keeps it until an
  // entry for the same field arrives with an empty value, so dropping the entry
  // left `status=open` on the request and "All" showed only the open queue.
  const { dataGridProps } = useDataGrid<RemovalRequestRecord>({
    resource: RESOURCE,
    filters: {
      permanent: [{ field: "status", operator: "eq" as const, value: status || undefined }],
    },
    queryOptions: {
      refetchInterval: REFRESH_INTERVAL_MS,
    },
  });

  const columns = React.useMemo<GridColDef<RemovalRequestRecord>[]>(
    () => [
      {
        field: "reference",
        headerName: "Reference",
        flex: 0.9,
        minWidth: 190,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => (
          <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
            {row.reference}
          </Typography>
        ),
      },
      {
        field: "hoursRemaining",
        headerName: "Deadline",
        flex: 0.8,
        minWidth: 170,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) =>
          row.resolvedAt ? (
            <Typography variant="caption" color="text.secondary">
              resolved {formatDateTime(row.resolvedAt)}
            </Typography>
          ) : (
            <Stack spacing={0.25}>
              {(() => {
                const deadline = deadlineState(row.dueAt);
                return (
                  <Chip
                    size="small"
                    label={deadline.label}
                    color={deadline.color}
                    variant={deadline.color === "default" ? "outlined" : "filled"}
                  />
                );
              })()}
              <Typography variant="caption" color="text.secondary">
                due {formatDateTime(row.dueAt)}
              </Typography>
            </Stack>
          ),
      },
      {
        field: "filerName",
        headerName: "Filer",
        flex: 0.9,
        minWidth: 170,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => (
          <Stack spacing={0.25}>
            <Typography variant="body2">{row.filerName}</Typography>
            <Typography variant="caption" color="text.secondary">
              {row.filerEmail}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "contentRef",
        headerName: "Content reference",
        flex: 1.4,
        minWidth: 240,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => (
          <Tooltip title={row.contentRef}>
            <Typography variant="body2" noWrap>
              {row.contentRef}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: "receivedAt",
        headerName: "Received",
        flex: 0.8,
        minWidth: 170,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => <Typography variant="body2">{formatDateTime(row.receivedAt)}</Typography>,
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.7,
        minWidth: 150,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => (
          <Stack spacing={0.25}>
            <Chip size="small" label={row.status} color={STATUS_COLOR[row.status]} />
            {row.assigneeName && (
              <Typography variant="caption" color="text.secondary">
                {row.assigneeName}
              </Typography>
            )}
          </Stack>
        ),
      },
      {
        field: "actions",
        headerName: "",
        width: 110,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => (
          <Button
            size="small"
            variant="outlined"
            aria-label={`Triage ${row.reference}`}
            onClick={() => setSelected(row.id)}
          >
            Triage
          </Button>
        ),
      },
    ],
    []
  );

  return (
    <List canCreate={false} title="Removal Requests">
      <Box sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          TAKE IT DOWN notice-and-removal intake. Every filing has 48 hours from receipt; the queue is ordered by
          deadline, soonest first. Changing a status never deletes anything — the log behind each request is the
          compliance record.
        </Typography>
      </Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField
          select
          size="small"
          label="Show"
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="open">Open (received + triaging)</MenuItem>
          <MenuItem value="">All</MenuItem>
          {STATUSES.map((value) => (
            <MenuItem key={value} value={value}>
              {value}
            </MenuItem>
          ))}
        </TextField>
        <Box sx={{ flex: 1 }} />
        <SlaExport />
      </Stack>
      <DataGrid {...dataGridProps} columns={columns} disableColumnFilter rowHeight={64} />
      <RemovalDetailDrawer id={selected} onClose={() => setSelected(null)} />
    </List>
  );
};
